/**
 * Riconoscimento delle casistiche d'errore del backend GraphQL.
 */

/**
 * EPWA-1023 / EPWA-1032 — quando l'intervallo interrogato copre un cambio di timezone della
 * centralina il backend risponde in errore:
 *
 *   pq: serial `OPIA0000003` has multiple timezones in the given time range! (P0001)
 *
 * L'errore e' **deterministico**: dipende da centralina e finestra, non da una condizione
 * transitoria. Ritentarlo produce richieste identiche destinate a fallire allo stesso modo.
 *
 * Il riconoscimento avviene sul testo perche' non c'e' altro: `extensions` porta solo dati di
 * sessione (`id`, `roles`, `srv`, `user`) e `extensions.code` e' `undefined`. Il `(P0001)` in
 * coda al messaggio e' il codice PostgreSQL generico di `RAISE EXCEPTION`, condiviso da ogni
 * errore sollevato a mano dal backend: da solo non identifica questa casistica.
 *
 * La regexp si ancora alla frase invariante e NON al prefisso `pq: serial \`...\``, che
 * contiene il seriale della centralina ed e' quindi variabile.
 */
const MULTIPLE_TIMEZONES = /has multiple timezones in the given time range/i;

/**
 * `true` se l'errore e' quello delle timezone multiple.
 *
 * Accetta sia un `ApolloError` (che espone `message` e `graphQLErrors`) sia un singolo errore
 * GraphQL: a seconda del punto della catena in cui si intercetta arriva l'uno o l'altro.
 */
export function isMultipleTimezonesError(err: any): boolean {
  if (MULTIPLE_TIMEZONES.test(err?.message ?? '')) {
    return true;
  }
  const graphQLErrors: any[] = err?.graphQLErrors ?? [];
  return graphQLErrors.some((e) => MULTIPLE_TIMEZONES.test(e?.message ?? ''));
}

/**
 * EPWA-1023 — Quanto resta a video il toast di questo errore, in millisecondi.
 *
 * Il default dei toast dell'app e' 5s, ma qui il messaggio e' lungo e spiega cosa fare:
 * a 5s non si fa in tempo a leggerlo (riscontrato durante il collaudo).
 */
export const TIMEZONE_TOAST_DURATION_MS = 12_000;

/**
 * Finestra di silenzio fra due notifiche dello stesso errore, in millisecondi.
 * Tenuta >= della durata del toast: altrimenti un secondo toast comparirebbe mentre il
 * primo e' ancora a video, che e' proprio il doppione che la guardia deve evitare.
 */
export const NOTIFY_WINDOW_MS = 15_000;

let lastNotifiedAt: number | null = null;

/**
 * `true` se l'errore delle timezone multiple va notificato all'utente adesso.
 *
 * In viewport mobile header e subheader sono entrambi montati e fanno la stessa query di
 * stato batteria: senza guardia lo stesso errore produrrebbe due toast sovrapposti. Lo
 * stesso vale quando piu' superfici falliscono insieme sullo stesso cambio di centralina.
 *
 * E' una funzione e non un servizio perche' lo stato e' un solo timestamp, e i chiamanti
 * vivono in injector diversi (componenti sotto PagesModule, effects nell'injector root).
 */
export function shouldNotifyTimezoneError(): boolean {
  const now = Date.now();
  if (lastNotifiedAt !== null && now - lastNotifiedAt < NOTIFY_WINDOW_MS) {
    return false;
  }
  lastNotifiedAt = now;
  return true;
}

/** Solo per i test: azzera la guardia fra un caso e l'altro. */
export function resetTimezoneErrorNotifications(): void {
  lastNotifiedAt = null;
}
